import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { MEETING_STATUS, RESOLUTION_STATUS, UPLOAD_BASE, fmtDateTH, fmtTime } from './aoShared'
import ThaiDateInput from '../components/ThaiDateInput'

// ธุรการ: รายละเอียดการประชุม — วาระ / ผู้เข้าประชุม / เช็คชื่อ / มติ / ไฟล์
export default function MeetingDetail() {
  const { mtId } = useParams()
  const [meeting, setMeeting] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // agenda editor
  const [showAgenda, setShowAgenda] = useState(false)
  const [agendaItems, setAgendaItems] = useState([])
  // invitees
  const [showInvite, setShowInvite] = useState(false)
  const [teachers, setTeachers] = useState([])
  const [selected, setSelected] = useState(new Set())
  const [filter, setFilter] = useState('')
  // resolution
  const [showResolution, setShowResolution] = useState(false)
  const [resForm, setResForm] = useState({ ms_text: '', ms_owner_t_id: '', ms_due: '', ag_id: '' })

  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axiosInstance.get(`/api/ao/meetings/${mtId}`)
      if (res.data.success) setMeeting(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [mtId])

  useEffect(() => { fetchData() }, [fetchData])

  const loadTeachers = async () => {
    if (teachers.length) return
    try {
      const res = await axiosInstance.get('/api/ao/teachers')
      if (res.data.success) setTeachers(res.data.data)
    } catch {
      setModalError('โหลดรายชื่อบุคลากรไม่สำเร็จ')
    }
  }

  const invited = useMemo(() => new Set((meeting?.invitees || []).map(i => i.tb_teacher_t_id)), [meeting])
  const visibleTeachers = useMemo(() => {
    const f = filter.trim().toLowerCase()
    return teachers.filter(t =>
      !f || t.t_name.toLowerCase().includes(f) || (t.hp_department || '').toLowerCase().includes(f))
  }, [teachers, filter])

  /* ---------- วาระ ---------- */
  const openAgenda = () => {
    setAgendaItems(meeting.agenda.length
      ? meeting.agenda.map(a => ({ ag_title: a.ag_title, ag_detail: a.ag_detail || '' }))
      : [
        { ag_title: 'เรื่องที่ประธานแจ้งให้ที่ประชุมทราบ', ag_detail: '' },
        { ag_title: 'รับรองรายงานการประชุมครั้งที่แล้ว', ag_detail: '' },
        { ag_title: 'เรื่องเสนอเพื่อทราบ', ag_detail: '' },
        { ag_title: 'เรื่องเสนอเพื่อพิจารณา', ag_detail: '' },
        { ag_title: 'เรื่องอื่น ๆ', ag_detail: '' },
      ])
    setModalError('')
    setShowAgenda(true)
  }

  const saveAgenda = async () => {
    setSaving(true)
    setModalError('')
    try {
      await axiosInstance.put(`/api/ao/meetings/${mtId}/agenda`, {
        agenda: agendaItems.filter(a => a.ag_title.trim()),
      })
      setShowAgenda(false)
      fetchData()
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกวาระไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  /* ---------- ผู้เข้าประชุม ---------- */
  const openInvite = () => {
    setSelected(new Set())
    setFilter('')
    setModalError('')
    setShowInvite(true)
    loadTeachers()
  }

  const handleInvite = async () => {
    if (!selected.size) {
      setModalError('กรุณาเลือกผู้เข้าประชุมอย่างน้อย 1 คน')
      return
    }
    setSaving(true)
    setModalError('')
    try {
      await axiosInstance.post(`/api/ao/meetings/${mtId}/invitees`, { t_ids: [...selected] })
      setShowInvite(false)
      fetchData()
    } catch (err) {
      setModalError(err.response?.data?.message || 'เชิญประชุมไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const setAttend = async (mi, attend) => {
    try {
      await axiosInstance.put(`/api/ao/meetings/${mtId}/invitees/${mi.mi_id}/attend`, { attend })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'บันทึกไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  /* ---------- มติ ---------- */
  const openResolution = () => {
    setResForm({ ms_text: '', ms_owner_t_id: '', ms_due: '', ag_id: '' })
    setModalError('')
    setShowResolution(true)
    loadTeachers()
  }

  const saveResolution = async () => {
    if (!resForm.ms_text.trim()) {
      setModalError('กรุณาระบุมติที่ประชุม')
      return
    }
    setSaving(true)
    setModalError('')
    try {
      await axiosInstance.post(`/api/ao/meetings/${mtId}/resolutions`, resForm)
      setShowResolution(false)
      fetchData()
    } catch (err) {
      setModalError(err.response?.data?.message || 'บันทึกมติไม่สำเร็จ')
    } finally {
      setSaving(false)
    }
  }

  const deleteResolution = async (r) => {
    const result = await Swal.fire({
      icon: 'warning', title: 'ลบมตินี้?', text: r.ms_text,
      showCancelButton: true, confirmButtonText: 'ลบ', cancelButtonText: 'ปิด', confirmButtonColor: '#dc3545',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.delete(`/api/ao/resolutions/${r.ms_id}`)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'ลบไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  /* ---------- ไฟล์ + สถานะ ---------- */
  const uploadFile = async (kind, label) => {
    const { value: file } = await Swal.fire({
      title: `แนบ${label}`,
      input: 'file',
      inputAttributes: { accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png' },
      showCancelButton: true,
      confirmButtonText: 'อัปโหลด',
      cancelButtonText: 'ปิด',
    })
    if (!file) return
    try {
      const fd = new FormData()
      fd.append('mt_title', meeting.mt_title)
      fd.append('mt_date', meeting.mt_date)
      fd.append('mt_time_start', meeting.mt_time_start || '')
      fd.append('mt_time_end', meeting.mt_time_end || '')
      fd.append('mt_place', meeting.mt_place || '')
      fd.append('file_kind', kind)
      fd.append('file', file)
      await axiosInstance.put(`/api/ao/meetings/${mtId}`, fd)
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'อัปโหลดไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const setStatus = async (status, label) => {
    const result = await Swal.fire({
      icon: 'question', title: `เปลี่ยนสถานะเป็น "${label}"?`,
      showCancelButton: true, confirmButtonText: 'ยืนยัน', cancelButtonText: 'ปิด',
    })
    if (!result.isConfirmed) return
    try {
      await axiosInstance.put(`/api/ao/meetings/${mtId}`, {
        mt_title: meeting.mt_title,
        mt_date: meeting.mt_date,
        mt_time_start: meeting.mt_time_start || '',
        mt_time_end: meeting.mt_time_end || '',
        mt_place: meeting.mt_place || '',
        mt_status: status,
      })
      fetchData()
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'เปลี่ยนสถานะไม่สำเร็จ', text: err.response?.data?.message || '' })
    }
  }

  const st = meeting ? (MEETING_STATUS[meeting.mt_status] || MEETING_STATUS.scheduled) : null
  const fileLink = (f) => `${UPLOAD_BASE}/uploads/ao_docs/${f}`

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <Link to="/admin-office/meetings" className="text-decoration-none me-2" title="กลับงานประชุม">
                <i className="ri-arrow-left-line" />
              </Link>
              <i className="ri-team-line me-2 text-primary" />
              ประชุมครั้งที่ {meeting?.mt_full_no || ''}
            </h4>
            {meeting && (
              <div className="d-flex gap-2 flex-wrap">
                <Button as={Link} to={`/admin-office/meetings/${mtId}/print-invite`} variant="outline-secondary" size="sm">
                  <i className="ri-printer-line me-1" />หนังสือเชิญ+วาระ
                </Button>
                <Button as={Link} to={`/admin-office/meetings/${mtId}/print-report`} variant="outline-secondary" size="sm">
                  <i className="ri-printer-line me-1" />รายงานการประชุม
                </Button>
                {meeting.mt_status === 'scheduled' && (
                  <Button variant="success" size="sm" onClick={() => setStatus('done', 'ประชุมแล้ว')}>
                    <i className="ri-check-double-line me-1" />ปิดการประชุม
                  </Button>
                )}
              </div>
            )}
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : !meeting ? (
            <Alert variant="warning">ไม่พบการประชุม</Alert>
          ) : (
            <Row className="g-3">
              <Col lg={5}>
                <Card className="card-one mb-3">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h6" className="mb-0">รายละเอียด</Card.Title>
                    <Badge bg={st.bg}>{st.label}</Badge>
                  </Card.Header>
                  <Card.Body className="fs-sm">
                    <div className="fw-semibold mb-1">{meeting.mt_title}</div>
                    <div><span className="text-muted">วันที่:</span> {fmtDateTH(meeting.mt_date)}
                      {meeting.mt_time_start ? ` เวลา ${fmtTime(meeting.mt_time_start)} - ${fmtTime(meeting.mt_time_end)}` : ''}</div>
                    <div><span className="text-muted">สถานที่:</span> {meeting.mt_place || '-'}</div>
                    <div className="mt-2 pt-2 border-top d-flex flex-column gap-1">
                      {[
                        ['invite', 'หนังสือเชิญ', meeting.mt_invite_file],
                        ['minutes', 'บันทึกการประชุม', meeting.mt_minutes_file],
                        ['report', 'รายงานการประชุม', meeting.mt_report_file],
                      ].map(([kind, label, f]) => (
                        <div key={kind} className="d-flex align-items-center gap-2">
                          <span className="text-muted" style={{ width: 140 }}>{label}:</span>
                          {f ? <a href={fileLink(f)} target="_blank" rel="noreferrer"><i className="ri-attachment-2" /> เปิดไฟล์</a> : <span className="text-muted">-</span>}
                          <Button size="sm" variant="link" className="p-0 fs-sm" onClick={() => uploadFile(kind, label)}>
                            {f ? 'เปลี่ยน' : 'แนบไฟล์'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>

                <Card className="card-one">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h6" className="mb-0">ระเบียบวาระ</Card.Title>
                    <Button size="sm" variant="outline-primary" onClick={openAgenda}>
                      <i className="ri-edit-line me-1" />จัดวาระ
                    </Button>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {meeting.agenda.length === 0 ? (
                      <div className="text-center text-muted py-3 fs-sm">ยังไม่มีวาระ</div>
                    ) : (
                      <Table className="mb-0 fs-sm">
                        <tbody>
                          {meeting.agenda.map(a => (
                            <tr key={a.ag_id}>
                              <td style={{ width: 80 }} className="text-muted">วาระที่ {a.ag_order}</td>
                              <td>
                                <div className="fw-semibold">{a.ag_title}</div>
                                {a.ag_detail && <div className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{a.ag_detail}</div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={7}>
                <Card className="card-one mb-3">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h6" className="mb-0">
                      ผู้เข้าประชุม ({meeting.invitees.length})
                      {meeting.invitees.some(i => i.mi_attend !== null) && (
                        <span className="text-muted fw-normal ms-2 fs-sm">
                          มา {meeting.invitees.filter(i => i.mi_attend === 1).length} คน
                        </span>
                      )}
                    </Card.Title>
                    <Button size="sm" variant="outline-primary" onClick={openInvite}>
                      <i className="ri-user-add-line me-1" />เชิญเพิ่ม
                    </Button>
                  </Card.Header>
                  <Card.Body className="p-0" style={{ maxHeight: 300, overflowY: 'auto' }}>
                    {meeting.invitees.length === 0 ? (
                      <div className="text-center text-muted py-3 fs-sm">ยังไม่ได้เชิญผู้เข้าประชุม</div>
                    ) : (
                      <Table hover className="mb-0 fs-sm">
                        <tbody>
                          {meeting.invitees.map((i, idx) => (
                            <tr key={i.mi_id}>
                              <td style={{ width: 40 }}>{idx + 1}</td>
                              <td>{i.t_name}</td>
                              <td style={{ width: 180 }} className="text-end">
                                <div className="btn-group btn-group-sm">
                                  <Button variant={i.mi_attend === 1 ? 'success' : 'outline-success'} size="sm"
                                    onClick={() => setAttend(i, i.mi_attend === 1 ? null : true)}>มา</Button>
                                  <Button variant={i.mi_attend === 0 ? 'danger' : 'outline-danger'} size="sm"
                                    onClick={() => setAttend(i, i.mi_attend === 0 ? null : false)}>ไม่มา</Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>

                <Card className="card-one">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <Card.Title as="h6" className="mb-0">มติที่ประชุม ({meeting.resolutions.length})</Card.Title>
                    <Button size="sm" variant="outline-primary" onClick={openResolution}>
                      <i className="ri-add-line me-1" />บันทึกมติ
                    </Button>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {meeting.resolutions.length === 0 ? (
                      <div className="text-center text-muted py-3 fs-sm">ยังไม่มีมติ</div>
                    ) : (
                      <Table responsive hover className="mb-0 fs-sm">
                        <thead className="table-light">
                          <tr>
                            <th>มติ</th>
                            <th style={{ width: 140 }}>ผู้รับผิดชอบ</th>
                            <th style={{ width: 110 }}>กำหนดเสร็จ</th>
                            <th style={{ width: 130 }}>สถานะ</th>
                            <th style={{ width: 50 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {meeting.resolutions.map(r => {
                            const rs = RESOLUTION_STATUS[r.ms_status] || RESOLUTION_STATUS.pending
                            return (
                              <tr key={r.ms_id}>
                                <td>
                                  <div>{r.ms_text}</div>
                                  {r.ms_result && <div className="text-muted" style={{ fontSize: '0.75rem' }}>ผล: {r.ms_result}</div>}
                                </td>
                                <td>{r.owner_name || '-'}</td>
                                <td>{fmtDateTH(r.ms_due)}</td>
                                <td><Badge bg={rs.bg}>{rs.label}</Badge></td>
                                <td className="text-end">
                                  <Button size="sm" variant="outline-danger" title="ลบ" onClick={() => deleteResolution(r)}>
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      <Footer />

      {/* Agenda modal */}
      <Modal show={showAgenda} onHide={() => setShowAgenda(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">จัดระเบียบวาระการประชุม</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
          {agendaItems.map((a, i) => (
            <div key={i} className="border rounded p-2 mb-2">
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="text-muted fs-sm" style={{ width: 60 }}>วาระที่ {i + 1}</span>
                <Form.Control size="sm" value={a.ag_title} placeholder="หัวข้อวาระ"
                  onChange={e => setAgendaItems(items => items.map((x, j) => j === i ? { ...x, ag_title: e.target.value } : x))} />
                <Button size="sm" variant="outline-danger"
                  onClick={() => setAgendaItems(items => items.filter((_, j) => j !== i))}>
                  <i className="ri-delete-bin-line" />
                </Button>
              </div>
              <Form.Control size="sm" as="textarea" rows={1} value={a.ag_detail} placeholder="รายละเอียด (ไม่บังคับ)"
                onChange={e => setAgendaItems(items => items.map((x, j) => j === i ? { ...x, ag_detail: e.target.value } : x))} />
            </div>
          ))}
          <Button size="sm" variant="outline-primary" onClick={() => setAgendaItems(items => [...items, { ag_title: '', ag_detail: '' }])}>
            <i className="ri-add-line me-1" />เพิ่มวาระ
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowAgenda(false)}>ยกเลิก</Button>
          <Button variant="primary" size="sm" disabled={saving} onClick={saveAgenda}>
            {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
            บันทึกวาระ
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invite modal */}
      <Modal show={showInvite} onHide={() => setShowInvite(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">เชิญผู้เข้าประชุม</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
          <div className="d-flex gap-2 mb-2 align-items-center">
            <Button variant="outline-primary" size="sm"
              onClick={() => setSelected(new Set(teachers.filter(t => !invited.has(t.t_id)).map(t => t.t_id)))}>
              <i className="ri-team-line me-1" />ทั้งโรงเรียน
            </Button>
            <Form.Control size="sm" placeholder="ค้นหาชื่อ/ฝ่าย" value={filter} onChange={e => setFilter(e.target.value)} />
            <span className="fs-sm text-muted text-nowrap">เลือกแล้ว {selected.size} คน</span>
          </div>
          <div className="border rounded" style={{ maxHeight: 300, overflowY: 'auto' }}>
            {visibleTeachers.map(t => {
              const done = invited.has(t.t_id)
              return (
                <Form.Check key={t.t_id} type="checkbox" className="px-3 py-1 border-bottom ms-3"
                  id={`inv-t-${t.t_id}`}
                  label={<span className={done ? 'text-muted' : ''}>{t.t_name}{t.hp_department ? ` · ${t.hp_department}` : ''}{done ? ' (เชิญแล้ว)' : ''}</span>}
                  checked={selected.has(t.t_id)}
                  disabled={done}
                  onChange={() => setSelected(prev => {
                    const next = new Set(prev)
                    if (next.has(t.t_id)) next.delete(t.t_id)
                    else next.add(t.t_id)
                    return next
                  })} />
              )
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowInvite(false)}>ยกเลิก</Button>
          <Button variant="primary" size="sm" disabled={saving} onClick={handleInvite}>
            {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-send-plane-line me-1" />}
            เชิญประชุม ({selected.size} คน)
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Resolution modal */}
      <Modal show={showResolution} onHide={() => setShowResolution(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">บันทึกมติที่ประชุม</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger" className="py-2">{modalError}</Alert>}
          <Form.Group className="mb-2">
            <Form.Label className="fs-sm mb-1">มติ <span className="text-danger">*</span></Form.Label>
            <Form.Control size="sm" as="textarea" rows={2} value={resForm.ms_text}
              onChange={e => setResForm(f => ({ ...f, ms_text: e.target.value }))} />
          </Form.Group>
          <Row className="g-2 mb-2">
            <Col xs={7}>
              <Form.Label className="fs-sm mb-1">วาระที่เกี่ยวข้อง</Form.Label>
              <Form.Select size="sm" value={resForm.ag_id}
                onChange={e => setResForm(f => ({ ...f, ag_id: e.target.value }))}>
                <option value="">— ไม่ระบุ —</option>
                {(meeting?.agenda || []).map(a => (
                  <option key={a.ag_id} value={a.ag_id}>วาระที่ {a.ag_order}: {a.ag_title}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={5}>
              <Form.Label className="fs-sm mb-1">กำหนดเสร็จ</Form.Label>
              <ThaiDateInput size="sm" value={resForm.ms_due}
                onChange={v => setResForm(f => ({ ...f, ms_due: v }))} />
            </Col>
          </Row>
          <Form.Group>
            <Form.Label className="fs-sm mb-1">ผู้รับผิดชอบ <span className="text-muted">— จะได้รับแจ้งเตือน</span></Form.Label>
            <Form.Select size="sm" value={resForm.ms_owner_t_id}
              onChange={e => setResForm(f => ({ ...f, ms_owner_t_id: e.target.value }))}>
              <option value="">— ไม่ระบุ —</option>
              {teachers.map(t => <option key={t.t_id} value={t.t_id}>{t.t_name}</option>)}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowResolution(false)}>ยกเลิก</Button>
          <Button variant="primary" size="sm" disabled={saving} onClick={saveResolution}>
            {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}
            บันทึกมติ
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
